import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
} from "recharts";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Card, Button, Input, Select, Modal, Segmented, Spinner } from "./ui";
import { formatMoney } from "../lib/utils";
import { RANGES, accountTypesFor, accountTypeLabel, holdingClassKey } from "../lib/portfolio";
import AssetsCard from "./AssetsCard";
import RealizedPnL from "./RealizedPnL";
import AllocationDonut from "./AllocationDonut";
import AccountDetail from "./AccountDetail";
import { HoldingMeta, ClassBadge, ExpiryBadge, expiryStatus } from "./HoldingMeta";
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Wallet, TrendingUp, TrendingDown, ChevronRight, RotateCcw, Link2, Check, Layers, X, AlertTriangle,
} from "lucide-react";

const POS = "#10B981";
const NEG = "#EF4444";

function ChangeText({ value, pct, currency, suffix, hidden }) {
  const up = (value ?? 0) >= 0;
  const color = up ? POS : NEG;
  return (
    <div className="flex items-center gap-2 text-sm" style={{ color }}>
      <span>{hidden ? "••••" : (up ? "+" : "−") + formatMoney(Math.abs(value || 0), currency, { decimals: 2 }).replace(/^-/, "")}</span>
      {pct !== null && pct !== undefined && (
        <span className="opacity-80">{up ? "+" : ""}{pct}%</span>
      )}
      {suffix && <span className="text-text-secondary">{suffix}</span>}
    </div>
  );
}

export default function PortfolioView({ entity }) {
  const { t, lang, canEdit, toast } = useApp();
  const [summary, setSummary] = useState(null);
  const [perf, setPerf] = useState(null);
  const [range, setRange] = useState("3M");
  const [mode, setMode] = useState("networth");
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(() => localStorage.getItem("portfolio_hide") === "1");
  const [accountModal, setAccountModal] = useState(null); // {} for new, account for edit
  const [classFilter, setClassFilter] = useState(null); // donut-driven asset-class filter
  // selected account lives in the URL (?account=) so browser back/refresh keep the Portfolio tab
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAccount = searchParams.get("account");
  const openAccount = (id) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("tab", "holdings");
    next.set("account", id);
    return next;
  });
  const closeAccount = () => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.delete("account");
    return next;
  });
  const [pendingDelAcc, setPendingDelAcc] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const copyAccountLink = async (id) => {
    const url = `${window.location.origin}${window.location.pathname}?tab=holdings&account=${id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopiedId(id);
    toast(t("portfolio_link_copied"));
    setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
  };
  const [pendingReset, setPendingReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [ledgerKey, setLedgerKey] = useState(0);
  const refreshLedger = () => setLedgerKey((k) => k + 1);

  const base = summary?.base_currency || "EUR";
  const money = (v, dec = 0) => (hidden ? "••••" : formatMoney(v, base, { decimals: dec }));

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolio/summary?entity_id=${entity.id}`);
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [entity.id]);

  const loadPerf = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolio/performance?entity_id=${entity.id}&range=${range}&mode=${mode}`);
      setPerf(data);
    } catch {
      setPerf(null);
    }
  }, [entity.id, range, mode]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadPerf(); }, [loadPerf]);

  const toggleHide = () => {
    setHidden((h) => { localStorage.setItem("portfolio_hide", h ? "0" : "1"); return !h; });
  };

  const changeEntityCurrency = async (cur) => {
    await api.put("/portfolio/entity-currency", { entity_id: entity.id, currency: cur || "inherit" });
    toast(t("portfolio_currency_saved"));
    loadSummary(); loadPerf();
  };

  const headlineValue = mode === "networth"
    ? summary?.totals?.net_worth
    : summary?.totals?.investments_value;

  const up = (perf?.change ?? 0) >= 0;
  const lineColor = up ? POS : NEG;

  const deleteAccount = async () => {
    if (!pendingDelAcc) return;
    await api.delete(`/portfolio/accounts/${pendingDelAcc.id}`);
    setPendingDelAcc(null);
    loadSummary(); loadPerf(); refreshLedger();
  };

  const doReset = async () => {
    setResetting(true);
    try {
      await api.post("/portfolio/reset", { entity_id: entity.id });
      setPendingReset(false);
      toast(t("portfolio_reset_done"));
      loadSummary(); loadPerf(); refreshLedger();
    } finally { setResetting(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-text-secondary" /></div>;
  }

  if (selectedAccount) {
    return (
      <AccountDetail entity={entity} accountId={selectedAccount} hidden={hidden}
        accounts={summary?.accounts || []}
        onBack={closeAccount}
        onChanged={() => { loadSummary(); loadPerf(); refreshLedger(); }} />
    );
  }

  return (
    <div data-testid="portfolio-view">
      {/* Header: toggle + headline value + change */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <Segmented
            testid="portfolio-mode"
            value={mode}
            onChange={setMode}
            options={[
              { value: "networth", label: t("portfolio_net_worth") },
              { value: "investments", label: t("portfolio_investments") },
            ]}
          />
          <div className="mt-3 text-4xl font-heading font-semibold tracking-tight" data-testid="portfolio-headline">
            {money(headlineValue, 2)}
          </div>
          {perf && (
            <div className="mt-1">
              <ChangeText value={perf.change} pct={perf.change_pct} currency={base}
                hidden={hidden} suffix={`${t("portfolio_past")} ${range}`} />
            </div>
          )}
          {summary?.totals && (
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-text-secondary">
              {summary.totals.total_gain_pct !== null && summary.totals.total_gain_pct !== undefined && (
                <span data-testid="portfolio-total-gain">
                  {t("portfolio_total_gain")}:{" "}
                  <span style={{ color: summary.totals.total_gain >= 0 ? POS : NEG }}>
                    {hidden ? "••••" : `${summary.totals.total_gain >= 0 ? "+" : ""}${formatMoney(summary.totals.total_gain, base, { decimals: 0 })}`}
                    {" "}({summary.totals.total_gain_pct >= 0 ? "+" : ""}{summary.totals.total_gain_pct}%)
                  </span>
                </span>
              )}
              {!!summary.totals.realized_gain && (
                <span data-testid="portfolio-realized-gain">
                  {t("activity_realized")}:{" "}
                  <span style={{ color: summary.totals.realized_gain >= 0 ? POS : NEG }}>
                    {hidden ? "••••" : `${summary.totals.realized_gain >= 0 ? "+" : ""}${formatMoney(summary.totals.realized_gain, base, { decimals: 0 })}`}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && summary?.currencies && (
            <select
              value={summary.currency_override || ""}
              onChange={(e) => changeEntityCurrency(e.target.value)}
              data-testid="portfolio-currency-select"
              className="text-xs px-2 py-2 rounded-lg border bg-surface text-text-secondary hover:text-text-primary transition-colors cursor-pointer focus:outline-none">
              <option value="">{t("portfolio_currency_inherit")} ({base})</option>
              {summary.currencies.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          <button onClick={toggleHide} data-testid="portfolio-hide-toggle"
            title={hidden ? t("portfolio_price") : t("portfolio_price")}
            className="p-2 rounded-lg border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors">
            {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          {canEdit && (
            <button onClick={() => setPendingReset(true)} data-testid="portfolio-reset-button"
              title={t("portfolio_reset")}
              className="p-2 rounded-lg border text-text-secondary hover:text-red-400 hover:bg-surface-hover transition-colors">
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Performance chart */}
      <Card className="!p-3 mb-3">
        <div style={{ width: "100%", height: 240 }} data-testid="portfolio-chart">
          {perf?.series?.length > 1 ? (
            <ResponsiveContainer>
              <AreaChart data={perf.series} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis domain={["auto", "auto"]} hide />
                <Tooltip
                  contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
                  labelStyle={{ color: "var(--text-secondary)" }}
                  formatter={(v) => [hidden ? "••••" : formatMoney(v, base, { decimals: 0 }), mode === "networth" ? t("portfolio_net_worth") : t("portfolio_investments")]}
                />
                <Area type="monotone" dataKey="value" stroke={lineColor} strokeWidth={2}
                  fill="url(#perfFill)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-text-secondary">
              {t("portfolio_no_holdings")}
            </div>
          )}
        </div>
      </Card>

      {/* Range tabs */}
      <div className="flex items-center justify-center gap-1 mb-6" data-testid="portfolio-ranges">
        {RANGES.map((r) => (
          <button key={r} onClick={() => setRange(r)} data-testid={`portfolio-range-${r}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${range === r ? "bg-text-primary text-background" : "text-text-secondary hover:text-text-primary"}`}>
            {r}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-text-secondary mb-5" data-testid="portfolio-data-note">
        {t("portfolio_data_note")}
        {summary?.prices_as_of && (
          <span data-testid="portfolio-prices-as-of">
            {" · "}{t("portfolio_prices_as_of")}{" "}
            {new Date(summary.prices_as_of).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </p>

      {/* Accounts + Allocation */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Accounts */}
        <Card data-testid="portfolio-accounts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-text-secondary" />
              <h3 className="font-heading font-semibold">{t("portfolio_accounts")}</h3>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setAccountModal({})} data-testid="add-account-button">
                <Plus className="w-4 h-4 mr-1" /> {t("portfolio_add_account")}
              </Button>
            )}
          </div>
          {(summary?.accounts || []).length === 0 ? (
            <p className="text-sm text-text-secondary py-4">{t("portfolio_no_accounts")}</p>
          ) : (
            <div className="space-y-2">
              {summary.accounts.map((a) => (
                <div key={a.id} role="button" tabIndex={0} onClick={() => openAccount(a.id)}
                  className="rounded-xl border px-3.5 py-3 flex items-center gap-3 group cursor-pointer hover:bg-surface-hover transition-colors" data-testid={`account-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <div className="text-[11px] text-text-secondary truncate">
                      {accountTypeLabel(a.type, a.custom_label, entity.country, lang)}
                      {" · "}{t("portfolio_cash")} {money(a.cash)} · {t("portfolio_securities")} {money(a.securities_value)}
                    </div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums">{money(a.total)}</div>
                  <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => copyAccountLink(a.id)} title={t("portfolio_copy_link")}
                      data-testid={`account-copy-link-${a.id}`}
                      className="text-text-secondary hover:text-text-primary opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                      {copiedId === a.id ? <Check className="w-3.5 h-3.5" style={{ color: POS }} /> : <Link2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-secondary shrink-0" />
                  {canEdit && (
                    <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setAccountModal(a)} data-testid={`account-edit-${a.id}`} className="text-text-secondary hover:text-text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setPendingDelAcc(a)} data-testid={`account-delete-${a.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-text-secondary mt-3">{t("portfolio_open_account_hint")}</p>
        </Card>

        {/* Allocation donut */}
        <AllocationDonut allocation={summary?.allocation} base={base} hidden={hidden}
          onSelect={setClassFilter} activeKey={classFilter} />
      </div>

      {/* Entity-level holdings list (click a donut class to filter) */}
      <HoldingsCard summary={summary} base={base} hidden={hidden} lang={lang}
        classFilter={classFilter} onClearFilter={() => setClassFilter(null)} t={t} />

      {mode === "networth" && (
        <AssetsCard entity={entity} base={base} hidden={hidden} refreshKey={ledgerKey}
          accounts={summary?.accounts || []}
          onChange={() => { loadSummary(); loadPerf(); refreshLedger(); }} />
      )}

      {mode === "networth" && (
        <RealizedPnL entity={entity} base={base} hidden={hidden} refreshKey={ledgerKey} />
      )}

      {accountModal && (
        <AccountModal entity={entity} account={accountModal.id ? accountModal : null}
          onClose={() => setAccountModal(null)}
          onSaved={() => { setAccountModal(null); loadSummary(); loadPerf(); }} />
      )}

      {pendingDelAcc && (
        <Modal open onClose={() => setPendingDelAcc(null)} title={pendingDelAcc.name}
          footer={<>
            <Button variant="outline" onClick={() => setPendingDelAcc(null)} data-testid="account-delete-cancel">{t("cancel")}</Button>
            <Button onClick={deleteAccount} className="!bg-red-500 hover:!bg-red-600" data-testid="account-delete-confirm">{t("delete")}</Button>
          </>}>
          <p className="text-sm text-text-secondary">{t("portfolio_delete_account_confirm")}</p>
        </Modal>
      )}
      {pendingReset && (
        <Modal open onClose={() => setPendingReset(false)} title={t("portfolio_reset")}
          footer={<>
            <Button variant="outline" onClick={() => setPendingReset(false)} data-testid="portfolio-reset-cancel">{t("cancel")}</Button>
            <Button onClick={doReset} disabled={resetting} className="!bg-red-500 hover:!bg-red-600" data-testid="portfolio-reset-confirm">{resetting ? t("loading") : t("portfolio_reset_confirm_btn")}</Button>
          </>}>
          <p className="text-sm text-text-secondary">{t("portfolio_reset_confirm")}</p>
        </Modal>
      )}
    </div>
  );
}

const POS2 = "#10B981";
const NEG2 = "#EF4444";

// Entity-level holdings list across all accounts, filterable by donut asset class.
function HoldingsCard({ summary, base, hidden, lang, classFilter, onClearFilter, t }) {
  const holdings = (summary?.holdings || []).filter((h) => h.symbol);
  if (!holdings.length) return null;
  const accName = {};
  (summary?.accounts || []).forEach((a) => { accName[a.id] = a.name; });
  const rows = (classFilter ? holdings.filter((h) => holdingClassKey(h.type) === classFilter) : holdings)
    .slice().sort((a, b) => (b.value || 0) - (a.value || 0));
  const money = (v, dec = 0) => (hidden ? "••••" : formatMoney(v, base, { decimals: dec }));
  const expiringCount = holdings.filter((h) => expiryStatus(h)).length;

  return (
    <Card data-testid="holdings-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-semibold">{t("portfolio_holdings_title")}</h3>
          <span className="text-xs text-text-secondary">{rows.length}</span>
          {expiringCount > 0 && (
            <span data-testid="holdings-expiring-count" title={t("portfolio_expiring_soon")}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium"
              style={{ color: "#F59E0B", background: "#F59E0B1A" }}>
              <AlertTriangle className="w-3 h-3" /> {expiringCount} {t("portfolio_expiring_header")}
            </span>
          )}
        </div>
        {classFilter && (
          <button onClick={onClearFilter} data-testid="holdings-clear-filter"
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border hover:bg-surface-hover transition-colors">
            {t("portfolio_filtered_by")} <ClassBadge type={classFilter === "stocks" ? "stock" : classFilter} />
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="space-y-1.5" data-testid="holdings-list">
        {rows.map((h) => (
          <div key={h.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`holding-row-${h.symbol}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{h.name}</span>
                <ClassBadge type={h.type} />
                <ExpiryBadge holding={h} />
              </div>
              <div className="text-[11px] text-text-secondary truncate">
                <span className="font-mono">{h.symbol}</span>
                {accName[h.account_id] ? ` · ${accName[h.account_id]}` : ""}
                {" · "}{h.quantity} × {h.price != null ? formatMoney(h.price, h.currency, { decimals: 2 }) : "—"}
              </div>
              <HoldingMeta holding={h} />
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold tabular-nums">{money(h.value)}</div>
              {h.gain != null && (
                <div className="text-[11px] tabular-nums" style={{ color: h.gain >= 0 ? POS2 : NEG2 }}>
                  {h.gain >= 0 ? "+" : "−"}{money(Math.abs(h.gain))}{h.gain_pct != null ? ` (${h.gain_pct >= 0 ? "+" : ""}${h.gain_pct}%)` : ""}
                </div>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-text-secondary py-3 text-center" data-testid="holdings-empty">{t("portfolio_no_holdings")}</p>
        )}
      </div>
    </Card>
  );
}

function AccountModal({ entity, account, onClose, onSaved }) {
  const { t, lang } = useApp();
  const types = accountTypesFor(entity.country);
  const [form, setForm] = useState({
    name: account?.name || "",
    type: account?.type || types[0].value,
    custom_label: account?.custom_label || "",
    currency: account?.currency || (entity.country === "US" ? "USD" : "EUR"),
    cash: account?.cash ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const payload = { ...form, cash: Number(form.cash) || 0, entity_id: entity.id };
      if (account) await api.put(`/portfolio/accounts/${account.id}`, payload);
      else await api.post("/portfolio/accounts", payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={account ? t("portfolio_edit_account") : t("portfolio_add_account")}
      footer={<>
        <Button variant="outline" onClick={onClose} data-testid="account-cancel">{t("cancel")}</Button>
        <Button onClick={save} disabled={busy || !form.name.trim()} data-testid="account-save">{busy ? t("loading") : t("save")}</Button>
      </>}>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-text-secondary block mb-1">{t("portfolio_name")}</label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="CTO Boursorama" data-testid="account-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-text-secondary block mb-1">{t("portfolio_type")}</label>
            <Select value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="account-type-select">
              {types.map((tp) => <option key={tp.value} value={tp.value}>{lang === "fr" ? tp.label.fr : tp.label.en}</option>)}
            </Select>
          </div>
          <div>
            <label className="text-xs text-text-secondary block mb-1">{t("portfolio_currency")}</label>
            <Select value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="account-currency-select">
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="CHF">CHF</option>
            </Select>
          </div>
        </div>
        {form.type === "other" && (
          <div>
            <label className="text-xs text-text-secondary block mb-1">{t("portfolio_custom_label")}</label>
            <Input value={form.custom_label} onChange={(e) => set("custom_label", e.target.value)} data-testid="account-custom-label-input" />
          </div>
        )}
        <div>
          <label className="text-xs text-text-secondary block mb-1">{t("portfolio_cash")}</label>
          <Input type="number" value={form.cash} onChange={(e) => set("cash", e.target.value)} data-testid="account-cash-input" />
        </div>
      </div>
    </Modal>
  );
}

